import React from "react";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import { CodeTable, getCodeTableValuesByTableName } from "../redux/slice/codetableSlice";
import dayjs, { Dayjs } from "dayjs";
import { getDataFromCookies, setDataToCookies } from "./StorageUtils";
import { TokenString } from "../types/CommonTypes";
import { DateTime } from 'luxon';
import { formatMethodLabel } from "../components/operation/payment/PaymentList";
import {
  COMMISSION_TYPE_AGENCY_KEY,
  COMMISSION_TYPE_CODETABLE,
  COMMISSION_TYPE_OVERRIDE_KEY,
  COMMISSION_TYPE_V3_DISPLAY_FALLBACK,
  COMMISSION_TYPE_V3_KEY,
  ISR_COMMISSION_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_CATEGORY_CODETABLE,
  PHARMACY_CONFIG_KEYS,
  PHARMACY_CONFIG_BILL_TO_PATIENT,
  PHARMACY_CONFIG_BILL_TO_PRACTICE_PROVIDER,
} from "../constants/applicationConstants";

// calculate the discount prices
export const calculateValue = (discount: string, price: number) => {
  // let discountValue = parseFloat(discount.replace('%', '')) / 100;   // CS-1984: guard null/undefined below
  let discountValue = parseFloat((discount ?? "0").replace('%', '')) / 100;   // CS-1984: null/undefined → 0% (no crash); empty/"%" still NaN (separate, deferred)
  return (1 - discountValue) * price;
}

// format the price 
export const priceFormatter = (price: number) => {
  if (price && price > 0) {
    price = Math.round(price * 100) / 100;
    let formattedPrice = price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Ensure two digits before decimal for prices less than 10 commenting the logic as per new requirement
    // if (Math.round(price) < 10) {
    //   formattedPrice = '0' + formattedPrice; 
    // }
    return `$${formattedPrice}`;
  }
  return `$0.00`;
}

export const finalPriceFormatter = (price: number) => {
  if (price === null || price === undefined || Number.isNaN(price)) {
    return `$0.00`;
  }
  const rounded = Math.round(price * 100) / 100;
  const formattedPrice = Math.abs(rounded).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return rounded < 0 ? `-$${formattedPrice}` : `$${formattedPrice}`;
};

// task cs-647  use for priceFormatter value convert Number(0-9)
export const parseAmount = (value: string | number): number => {
  if (!value) return 0;

  if (typeof value === 'string') {
    const sanitizedValue = value.replace(/[^0-9.-]+/g, '');
    return parseFloat(sanitizedValue) || 0;
  }

  return parseFloat(value.toString()) || 0;
};
// task cs-647  end

//generate full name
export const fullName = (title: string | null | undefined, firstName: string | null | undefined, middleName: string | null | undefined, lastName: string | null | undefined): string => {
  const parts = [title, firstName, middleName, lastName].filter(part => part !== null && part !== undefined && part.trim() !== "");
  return parts.join(" ");
};

//format Data
export const formatDate = (date: Date | string | null, formatPattern: string = 'MM/DD/YYYY') => {
  if (!date) {
    return '';
  }
  return moment(date).format(formatPattern);
};

export const formatDateTime = (isoString: string | undefined | null) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC"
  }).replace(",", "");
};

export const formatShippingTrackingDateTime = (isoString: string): string => {
  const dt = DateTime.fromISO(isoString, { setZone: true });
  // const dt = DateTime.fromISO(isoString).toLocal(); // Remove setZone, convert to local

  if (!dt.isValid) {
    return 'N/A';
  }

  const month = dt.toFormat('LLL');
  const day = dt.toFormat('dd');
  const year = dt.toFormat('yy');
  const time = dt.toFormat('h:mm a');

  return `${month} ${day} ${year}, ${time}`;
};

export const formatShippingTrackingExpectedDateTime = (isoString: string): string => {
  const dt = DateTime.fromISO(isoString).toLocal();

  if (!dt.isValid) {
    return 'N/A';
  }

  const month = dt.toFormat('LLL');
  const day = dt.toFormat('dd');
  const year = dt.toFormat('yy');
  const time = dt.toFormat('h:mm a');

  return `${month} ${day} ${year}, ${time}`;
};

export const convertPriceToString = (price: any) => {
  return price ? price.toString().replace('$', '').replaceAll(',', '') : "";
}

export const isDateGivenRange = (to: Dayjs, from: Dayjs, date: Dayjs) => {
  return (date.isSame(from) || date.isAfter(from)) && (date.isSame(to) || date.isBefore(to));
}
// Sort by alphabetical order
export const sortNames = (info: any, sortingField: string) => {
  return info.slice().sort((a: any, b: any) => {
    const nameA = a[sortingField]?.trim().toLocaleLowerCase();
    const nameB = b[sortingField]?.trim().toLocaleLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
};

// hide an element
export const hideElement = (condition: boolean) => {
  if (condition) {
    return 'none';
  } else {
    return "";
  }
};

// Convert file data to base64 string
export const convertBase64 = (file: any) => {
  return new Promise((resolve) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = function () {
      resolve(reader.result);
    };
    reader.onerror = function (error) {
      console.log("Error: ", error);
    };
  });
};

// Format address
export const formatAddress = (
  streetAddress?: string | null,
  unitNumber?: string | null,
  city?: string | null,
  state?: string | null,
  zipCode?: string | null
) => {
  const addressParts = [streetAddress, unitNumber, city, state, zipCode];
  const filteredLAddressParts = addressParts.filter(part => part !== undefined && part !== null);
  return filteredLAddressParts?.join(", ");
};

// Hide Days and Months of Birthday
export const hideDayMonthOfBOD = (bod: string) => {
  if (!bod) return '';
  const bodParts = bod?.split("/");
  if (bodParts.length === 3 && bodParts[2].length === 4) {
    const year = bodParts[2];
    return `XX/XX/${year}`;
  };
  return '';
};


export const retrieveDataFromTokenCookie = (data: string) => {
  let obj: TokenString = {
    token: "",
    count: 0,
  };
  try {
    obj = JSON.parse(data)
  } catch {
  }
  return obj;
}

export const updateTokenToCookie = (value: any) => {
  const token = getDataFromCookies("token");
  let data: TokenString = retrieveDataFromTokenCookie(token);
  data.token = value;
  setDataToCookies("token", JSON.stringify(data));
}

export const getTokenFromCookie = () => {
  const token = getDataFromCookies("token");
  let data: TokenString = retrieveDataFromTokenCookie(token);
  return data.token;
}

export const updateWindowCountToCookie = (value: any) => {
  const token = getDataFromCookies("token");
  let data: TokenString = retrieveDataFromTokenCookie(token);
  data.count = value;
  setDataToCookies("token", JSON.stringify(data));
}

export const getWindowCountFromCookie = () => {
  const token = getDataFromCookies("token");
  let data: TokenString = retrieveDataFromTokenCookie(token);
  return data.count;
}

export const woundNameWithDimensions = (woundType: string, woundInfoKey: string, woundLength: string | number, woundWidth: string | number, woundDepth: string | number) => {
  if (woundType && woundInfoKey && woundLength && woundWidth && woundDepth) {
    const woundNumber = woundInfoKey.split('_').pop() || '1';
    const dimensions = `(${woundLength}cm x ${woundWidth}cm x ${woundDepth}cm)`;
    return `${woundType.toUpperCase().slice(0, 2)}-${woundNumber} ${dimensions}`;
  }
  return '';
}

export const formatWoundName = (woundType: string, woundInfoKey: string) => {
  if (woundType && woundInfoKey) {
    const woundNumber = woundInfoKey.split('_').pop() || '1';
    return `${woundType.toUpperCase().slice(0, 2)}-${woundNumber}`;
  }
  return '';
}

export const downloadBase64File = (
  base64String: string,
  fileName: string,
  mimeType: string
) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor); // Required for Firefox
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

//CS-1191 Common general funtion to calculate the commission amount based on rate and invoice amount

/**
 * Calculate commission amount from invoice amount and commission rate.
 * @param invoiceAmount - the total invoice amount
 * @param commissionRate - the commission rate as a percentage (e.g., 25.1234)
 * @returns number - the commission amount (up to 4 decimals)
 */
export function calculateCommissionAmount(invoiceAmount: number, commissionRate: number): number {
  if (
    typeof invoiceAmount !== 'number' ||
    typeof commissionRate !== 'number' ||
    isNaN(invoiceAmount) ||
    isNaN(commissionRate)
  ) {
    return 0;
  }
  const rawAmount = (invoiceAmount * commissionRate) / 100;
  return Math.round(rawAmount * 10000) / 10000; // 4 decimal precision
}

/**
 * Calculate commission rate from commission amount and invoice amount.
 * @param invoiceAmount - the total invoice amount
 * @param commissionAmount - the actual commission amount
 * @returns number - the commission rate (up to 4 decimals)
 */
export function calculateCommissionRate(invoiceAmount: number, commissionAmount: number): number {
  if (
    typeof invoiceAmount !== 'number' ||
    typeof commissionAmount !== 'number' ||
    invoiceAmount === 0 ||
    isNaN(invoiceAmount) ||
    isNaN(commissionAmount)
  ) {
    return 0;
  }

  const rawRate = (commissionAmount / invoiceAmount) * 100;
  return Math.round(rawRate * 10000) / 10000; // 4 decimal precision
}

/**
 * Format a number to 2 decimal places for UI display.
 * Optionally add a prefix (like '$') or suffix (like '%').
 * 
 * @param value - The numeric value to format
 * @param options - Optional configuration
 * @returns formatted string
 */
export function formatValue(
  value: number,
  options?: { prefix?: string; suffix?: string }
): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return `${options?.prefix || ''}0.00${options?.suffix || ''}`;
  }

  const formatted = value.toFixed(2);
  return `${options?.prefix || ''}${formatted}${options?.suffix || ''}`;
}


//CS-1297 - Start
export const extractInsuranceData = (infoArray: any[], type: string) => {
  return infoArray?.find(info => info.infoType === type && info.inactiveDate === null)?.patientinfoData || null;
};
//CS-1297 - end

export const getStatusColor = (status: string): string => {
  switch (status?.toUpperCase()) {
    case "DELIVERED":
    case "VERIFIED":
    case "ACTIVE":
    case "COMPLETED":
    case "CREATED":
      return "#1aa457"; // Green

    case "SHIPPED":
      return "#5dabff"; // Blue

    case "CANCELED":
    case "VOID":
    case "CANCELLED":
    case "CLOSED":
    case "INACTIVE":
      return "#dc3545"; // Red

    case "PROCESSING":
      return "#fd7e14"; // Orange

    case "DRAFTED":
      return "#6f50a8"; // Purple

    case "NEEDS MORE INFO":
      return "#e0a802"; // Yellowish
    case "NEEDS INFO":
      return "#e0a802"; // Yellowish
    case "PENDING":
      return "#d9dadcff";
    case "PRE-AUTH":
      return "#17a2b8";
    case "DELAYED":
      return "#ffc107";
    case "DENIED":
    case "FAILURE":
      return "#cb0000"; // Dark Red
    default:
      return "inherit"; // Default color for unknown statuses
  }
};

// CS-1790
export const getFacesheetStatusColor = (status: string): string => {
  switch (status?.toUpperCase()) {
    case "COMPLETED":
    case "EXECUTED":
      return "#1aa457"; // Green
    case "FAILED":
      return "#cb0000"; // Dark Red
    case "QUEUED":
      return "#6f50a8"; // Purple
    case "PROCESSING":
      return "#fd7e14"; // Orange
    default:
      return "inherit";
  }
};

export const formatAuditTimestamp = (timestamp: string) => {
  if (!timestamp) return { date: "", time: "" };

  const localTime = dayjs.utc(timestamp).local();

  return {
    date: localTime.format("MM/DD/YYYY"),
    time: localTime.format("hh:mm A"),
  };
};

export const convertUTCtoLocal = (lastUpdatedUTC: string) => {
  const localTime = formatAuditTimestamp(lastUpdatedUTC);
  const localTimeFormatted = localTime.date + " " + localTime.time;
  return localTimeFormatted;
};

//CS-1576 :Rendering dynamic color for status notification color
export const rederDynamicStatusColor = (invoiceStatus: string) => {
  switch (invoiceStatus) {
    case "PAID":
      return "#1aa457";
    case "RECEIVED":
      return "#1aa457";
    case "UNPAID":
      return "#d12626ff";
    case "FAILED":
      return "#d12626ff";
    case "PARTIAL":
      return "#d12626ff";
    // return "#3ea4edff";
    case "PROCESSING":
      return "#eb960fff";
    default:
      return "#d12626ff";
  }
}

//CS-1617 -Render Dynamic Aging Column 
export const renderDynamicAgingColumn = (data: any): JSX.Element => {
  const days = data?.invoiceLateDays;
  const countDays = data?.invoiceCountDays;
  const isPaidOrClosed = data?.invoiceStatus === "PAID" || data?.invoiceStatus === "CLOSED";

  // Shared layout style for consistent left-aligned rendering
  const wrapperStyle = (c: string) => ({
    display: "inline-flex" as const,
    alignItems: "center" as const,
    fontWeight: "bold" as const,
    whiteSpace: "nowrap" as const,
    fontVariantNumeric: "tabular-nums" as const,
    color: c,
  });

  // Helper to render dash with same two-column layout for alignment
  const renderDash = () => React.createElement(
    'span',
    { style: wrapperStyle("white") },
    React.createElement('span', { style: { width: 10, display: "inline-block" } }, ''),
    React.createElement('span', null, '-')
  );

  // No data fallback
  if (days === null || days === undefined || days === "") {
    return renderDash();
  }

  // For paid/closed, show dash
  if (isPaidOrClosed) {
    return renderDash();
  }

  // Determine color based on invoiceCountDays and invoiceLateDays
  let color = "";

  if (countDays === 60) {
    if (days >= 31) {
      color = "#5BE49B";
    } else if (days >= 1) {
      color = "#e0a802";
    } else {
      color = "#F44336";
    }
  } else if (countDays === 45) {
    if (days >= 16) {
      color = "#5BE49B";
    } else if (days >= 1) {
      color = "#e0a802";
    } else {
      color = "#F44336";
    }
  } else if (countDays === 30) {
    if (days > 9) {
      color = "#5BE49B";
    } else if (days >= 1) {
      color = "#e0a802";
    } else {
      color = "#F44336";
    }
  }

  // No matching countDays, show dash
  if (!color) {
    return renderDash();
  }

  const num = Number(days);
  const sign = num < 0 ? "−" : "";
  const absVal = Math.abs(num);

  // Render with aligned sign and number columns
  return React.createElement(
    'span',
    { style: wrapperStyle(color) },
    React.createElement(
      'span',
      { style: { width: 10, textAlign: "right" as const, display: "inline-block" } },
      sign
    ),
    React.createElement('span', null, absVal)
  );
}

//CS-1706 : Function to show compliance color

export const getComplianceColor = (score: number): string => {
  if (score >= 90) return "#5BE49B"
  if (score >= 70) return "#e0a802";
  return "#F44336";
}

export const formatPaymentMethod = (paymentMethod: string) => {
  return paymentMethod === "us_bank_account" ? "ACH" : paymentMethod ? formatMethodLabel(paymentMethod) : "";
}

export const getInitials = (name: string) =>
  (name ?? "U")
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

export const formatInboxTime = (ts: string) => {
  const now = dayjs();
  const time = dayjs(ts);
  const diffMins = now.diff(time, "minute");
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffMins < 1440 && now.isSame(time, "day")) {
    return `${Math.floor(diffMins / 60)}h ago`;
  }
  if (now.subtract(1, "day").isSame(time, "day")) {
    return "Yesterday";
  }
  return time.format("MMM DD");
};


/**
 * Maps the raw database category string to a human-friendly label used in UI filters.
 * @deprecated Use getCategoryDisplayLabel(allCodeTablesValuesMap, rawCategory) for dynamic labels.
 */
export const mapCategoryLabel = (rawCategory: string | null): string => {
  if (!rawCategory) return "Other";
  const cat = rawCategory.toUpperCase();

  // If the raw data contains CTP, map it to the 'Advanced' group
  if (cat.includes("CTP")) return PRODUCT_CATEGORY.ADVANCED;
  if (cat.includes("COLLAGEN") || cat.includes("CONSERVATIVE")) return PRODUCT_CATEGORY.CONSERVATIVE;

  if (cat.includes("ADVANCE")) {
    return PRODUCT_CATEGORY.ADVANCED;
  }
  if (cat.includes("STANDARD")) return "Standard";
  if (cat.includes("GENERAL")) return "General";

  return "Other";
};

export const getCategoryDisplayLabel = (allCodeTablesValuesMap: any, keyName: string | null): string => {
  if (!keyName) return "";
  const categoryCodes = getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE);

  if (Array.isArray(categoryCodes)) {
    const codeEntry = categoryCodes.find((c: CodeTable) => c.keyName === keyName);
    if (codeEntry) return codeEntry.displayValue;
  }

  // If codetable hasn't loaded yet, return the raw keyName as-is (avoids hardcoded fallback)
  return keyName;
};

/** True when the value carries letters and none of them are lowercase (e.g. "AGENCY"). */
const isUpperCaseValue = (value: string): boolean =>
  /[a-z]/i.test(value) && value === value.toUpperCase();

/** True when the value is the Primary Org type key, regardless of V3/v3 casing. */
const isV3KeyAlias = (value: string): boolean => value.toLowerCase() === "v3";

/**
 * Resolves a stored codetable keyName to its database displayValue.
 *
 * An exact keyName match returns the displayValue as authored in the database.
 * When the stored value only differs by casing — the same entity arrives as
 * "Agency" from one endpoint and "AGENCY" from another — the row is still
 * matched and the displayValue is re-cased to match the stored value, so an
 * uppercase source keeps rendering uppercase.
 *
 * Falls back to the stored value whenever the codetable has not loaded yet, has
 * no matching row, or the row carries no displayValue, so a missing or renamed
 * entry degrades to what the API sent instead of rendering blank.
 * The V3/v3 row falls back to COMMISSION_TYPE_V3_DISPLAY_FALLBACK ("V3") when the
 * codetable is missing or the row has no displayValue.
 */
export const getCodeTableDisplayLabel = (
  allCodeTablesValuesMap: any,
  tableName: string,
  keyName: string | null | undefined
): string => {
  if (!keyName) return "";
  const storedValue = String(keyName);
  const v3DisplayFallback =
    tableName === COMMISSION_TYPE_CODETABLE && isV3KeyAlias(storedValue)
      ? COMMISSION_TYPE_V3_DISPLAY_FALLBACK
      : "";

  const codes = getCodeTableValuesByTableName(allCodeTablesValuesMap, tableName);
  if (!Array.isArray(codes)) return v3DisplayFallback || storedValue;

  const exactMatch = codes.find(
    (c: CodeTable) => c?.keyName === storedValue
  );
  if (exactMatch?.displayValue) return exactMatch.displayValue;

  const caseInsensitiveMatch = codes.find(
    (c: CodeTable) =>
      typeof c?.keyName === "string" &&
      c.keyName.toLowerCase() === storedValue.toLowerCase()
  );
  if (caseInsensitiveMatch?.displayValue) {
    return isUpperCaseValue(storedValue)
      ? caseInsensitiveMatch.displayValue.toUpperCase()
      : caseInsensitiveMatch.displayValue;
  }

  return v3DisplayFallback || storedValue;
};

/**
 * Returns the code table's authored keyName for a stored/alias value, so request
 * bodies carry whatever the table currently stores instead of a client-side word.
 *
 * The row is looked up by keyName first (exact, then ignoring case, so "Override"
 * and "OVERRIDE" reach the same row). When that misses, the displayValue is used
 * as a second identifier: a keyName renamed in the database ("Override" ->
 * "Override1") is still recognised, and the renamed keyName is what gets sent.
 *
 * Falls back to the passed value when the table has not loaded or nothing matches,
 * so a payload is never emitted empty.
 */
export const getCodeTableKeyName = (
  allCodeTablesValuesMap: any,
  tableName: string,
  keyName: string | null | undefined
): string => {
  if (!keyName) return "";
  const storedValue = String(keyName);
  const v3RequestFallback = isV3KeyAlias(storedValue) ? COMMISSION_TYPE_V3_KEY : storedValue;

  const codes = getCodeTableValuesByTableName(allCodeTablesValuesMap, tableName);
  if (!Array.isArray(codes) || codes.length === 0) return v3RequestFallback;

  const target = storedValue.toLowerCase();
  const match =
    codes.find((c: CodeTable) => c?.keyName === storedValue) ??
    codes.find(
      (c: CodeTable) =>
        typeof c?.keyName === "string" && c.keyName.toLowerCase() === target
    ) ??
    codes.find(
      (c: CodeTable) =>
        typeof c?.displayValue === "string" &&
        c.displayValue.toLowerCase() === target
    );

  return match?.keyName || v3RequestFallback;
};

/** True when `value` is the same code-table key as `alias`, ignoring case. */
export const matchesCodeTableKey = (
  allCodeTablesValuesMap: any,
  tableName: string,
  value: string | null | undefined,
  alias: string
): boolean => {
  if (!value || !alias) return false;
  const canonical = getCodeTableKeyName(allCodeTablesValuesMap, tableName, alias);
  const actual = String(value).toLowerCase();
  return actual === String(canonical).toLowerCase() || actual === String(alias).toLowerCase();
};

/** CommissionType key V3 → displayValue from codetable (fallback unchanged when table missing). */
export const getV3CommissionTypeDisplayLabel = (allCodeTablesValuesMap: any): string =>
  getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, COMMISSION_TYPE_V3_KEY);

/** Primary Org / V3 entity: matches the CommissionType row whether keyName is V3 or v3. */
export const isV3CommissionType = (
  allCodeTablesValuesMap: any,
  value: string | null | undefined
): boolean =>
  matchesCodeTableKey(
    allCodeTablesValuesMap,
    COMMISSION_TYPE_CODETABLE,
    value,
    COMMISSION_TYPE_V3_KEY
  );

/** Agency entity: matches the CommissionType row whether the value is "Agency" or "AGENCY". */
export const isAgencyCommissionType = (
  allCodeTablesValuesMap: any,
  value: string | null | undefined
): boolean =>
  matchesCodeTableKey(
    allCodeTablesValuesMap,
    COMMISSION_TYPE_CODETABLE,
    value,
    COMMISSION_TYPE_AGENCY_KEY
  );

/** Override entity: matches the CommissionType row whether the value is "Override" or "OVERRIDE". */
export const isOverrideCommissionType = (
  allCodeTablesValuesMap: any,
  value: string | null | undefined
): boolean =>
  matchesCodeTableKey(
    allCodeTablesValuesMap,
    COMMISSION_TYPE_CODETABLE,
    value,
    COMMISSION_TYPE_OVERRIDE_KEY
  );

/** ISR entity: matches CommissionType rows such as ISR, ISR1, etc. (managed on ISR Dashboard). */
export const isIsrCommissionType = (
  allCodeTablesValuesMap: any,
  value: string | null | undefined
): boolean => {
  if (!value) return false;
  if (matchesCodeTableKey(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, value, ISR_COMMISSION_TYPE)) {
    return true;
  }
  const canonical = getCodeTableKeyName(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, value);
  const key = String(canonical || value).toUpperCase();
  const isrKey = (ISR_COMMISSION_TYPE || "ISR").toUpperCase();
  return key === isrKey || key.startsWith("ISR");
};

/** CommissionType keyName for ISR agencies (list/save). Uses codetable when loaded; falls back to ISR. */
export const getIsrCommissionTypeKey = (allCodeTablesValuesMap: any): string => {
  const rows = getCodeTableValuesByTableName(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE);
  const match = rows.find(
    (row) => row?.keyName && isIsrCommissionType(allCodeTablesValuesMap, row.keyName)
  );
  return match?.keyName ? String(match.keyName) : ISR_COMMISSION_TYPE;
};

/** Dropdown options for ISR commission type (codetable-driven). */
export const getIsrCommissionTypeSelectOptions = (
  allCodeTablesValuesMap: any
): { label: string; value: string }[] => {
  const rows = getCodeTableValuesByTableName(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE);
  const isrRows = rows.filter(
    (row) => row?.keyName && isIsrCommissionType(allCodeTablesValuesMap, row.keyName)
  );
  if (!isrRows.length) {
    return [{ label: "ISR", value: ISR_COMMISSION_TYPE }];
  }
  return isrRows.map((row) => ({
    label:
      getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, row.keyName)
      || row.displayValue
      || row.keyName,
    value: row.keyName,
  }));
};

/**
 * CommissionType value for commission / payout / invoice request bodies.
 *
 * The word always comes from the codetable keyName — renaming the row in the
 * database changes what is sent, with no client change. Only the casing is
 * decided here: these endpoints bind to the CommissionAgencyType enum and to the
 * uppercase InvoiceAgency.agencyType column, so the resolved keyName is
 * upper-cased regardless of how it is authored in the database.
 *
 * Note this is NOT for the agency's own commissionType field (agency search /
 * create / update): that column takes the keyName verbatim, so those screens send
 * the dropdown value as-is.
 *
 * Falls back to the passed value when the codetable has not loaded, so payloads
 * never go out empty.
 */
export const toCommissionTypeRequestKey = (
  allCodeTablesValuesMap: any,
  value: string | null | undefined
): string => {
  if (!value) return "";
  const canonicalKey = getCodeTableKeyName(
    allCodeTablesValuesMap,
    COMMISSION_TYPE_CODETABLE,
    value
  );
  return (canonicalKey || String(value)).toUpperCase();
};

export const buildAvatarSrc = (doc?: { fileType?: string; content?: string } | null) =>
  doc
    ? `data:image/jpeg;base64,${doc}`
    : undefined;

// Reusable label component for modal form fields.
// `tokens` must be passed from `useModalStyles()` (for example: `tokens={tokens}`)
// so the text and required asterisk colors stay consistent with light/dark theme styles.
export const FieldLabel = ({
  label,
  required = false,
  tokens,
}: {
  label: string;
  required?: boolean;
  tokens: { textSecondary: string; accent: string };
}) =>
  React.createElement(
    Typography,
    {
      sx: {
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      },
    },
    label,
    required &&
      React.createElement(
        Box,
        { component: "span", sx: { color: tokens.accent } },
        "*"
      )
  );

/**
 * CS-2075 — maps a manufacturer name to sandsrx / genesis via lowercase contains.
 * Same order as the backend (genesis first). Explicit isPharmacy=false returns null.
 * Missing flag still matches by name so Rx billing works when the product has no isPharmacy field.
 */
export const resolvePharmacyKey = (
  manufacturerName?: string | null,
  isPharmacy?: boolean | null
): string | null => {
  if (!manufacturerName || isPharmacy === false) return null;
  const name = manufacturerName.toLowerCase();
  for (const key of PHARMACY_CONFIG_KEYS) {
    if (name.includes(key)) return key;
  }
  return null;
};

export type PharmacyConfig = {
  billTo?: {
    enabledValues?: string[];
    defaultValue?: string;
  };
};

const canonicalizeBillToValue = (raw?: string | null): string => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.toLowerCase() === PHARMACY_CONFIG_BILL_TO_PATIENT.toLowerCase()) {
    return PHARMACY_CONFIG_BILL_TO_PATIENT;
  }
  if (value.toLowerCase() === PHARMACY_CONFIG_BILL_TO_PRACTICE_PROVIDER.toLowerCase()) {
    return PHARMACY_CONFIG_BILL_TO_PRACTICE_PROVIDER;
  }
  return "";
};

const canonicalizeBillToList = (raw: unknown): string[] => {
  const items = Array.isArray(raw) ? raw : raw == null || raw === "" ? [] : [raw];
  const values: string[] = [];
  items.forEach((item) => {
    const value = canonicalizeBillToValue(item == null ? "" : String(item));
    if (value && !values.includes(value)) values.push(value);
  });
  return values;
};

export const pharmacyBillToFromConfig = (pharmacyConfig?: PharmacyConfig | null) => {
  const billTo = pharmacyConfig?.billTo as unknown;
  const nested = billTo && typeof billTo === "object" && !Array.isArray(billTo)
    ? (billTo as { enabledValues?: string[]; defaultValue?: string })
    : null;
  const enabledValues = canonicalizeBillToList(nested ? nested.enabledValues : billTo);
  const selected = canonicalizeBillToValue(nested?.defaultValue);
  return {
    enabledValues,
    defaultValue: enabledValues.includes(selected) ? selected : enabledValues[0] || "",
  };
};

export const toPharmacyConfig = (
  isPharmacy: boolean,
  enabledValues?: string[],
  defaultValue?: string
): PharmacyConfig => {
  if (!isPharmacy) return {};
  const values = canonicalizeBillToList(enabledValues);
  if (!values.length) return {};
  const selected = canonicalizeBillToValue(defaultValue);
  return {
    billTo: {
      enabledValues: values,
      defaultValue: values.includes(selected) ? selected : values[0],
    },
  };
};

export const pharmacyBillToRadioOptions = (pharmacyConfig?: PharmacyConfig | null) => {
  if (pharmacyConfig == null) return [];
  const { enabledValues } = pharmacyBillToFromConfig(pharmacyConfig);
  const values = enabledValues.length ? enabledValues : [PHARMACY_CONFIG_BILL_TO_PRACTICE_PROVIDER];
  return values.map((value) =>
    value === PHARMACY_CONFIG_BILL_TO_PATIENT
      ? { value: "patient", label: "Patient" }
      : { value: "clinic", label: "Provider / Practice" }
  );
};

export const pharmacyBillToRxDefault = (pharmacyConfig?: PharmacyConfig | null): "clinic" | "patient" | "" => {
  if (pharmacyConfig == null) return "";
  const { enabledValues, defaultValue } = pharmacyBillToFromConfig(pharmacyConfig);
  if (!enabledValues.length) return "clinic";
  return (defaultValue || enabledValues[0]) === PHARMACY_CONFIG_BILL_TO_PATIENT ? "patient" : "clinic";
};

